'use client';

/**
 * ConversationRatingCard — the ONE whole-conversation rating card reused by
 * all four triggers (idle / end_chat / handoff_wrap / menu — see page.tsx's
 * wiring and lib/conversation-feedback-ask.ts's shouldAskForFeedback for the
 * "should we even ask" decision each trigger consults before mounting this).
 * Built once, per this task's brief — none of the four call sites get their
 * own copy or their own submit logic; they just mount this with a
 * `conversationId`/`source` and, for the handoff_wrap case, a `handoffId`.
 *
 * Renders inline in the conversation lane (a plain StandardCard, no overlay/
 * modal chrome) — same "lives in the transcript, never blocks anything"
 * precedent as HandoffQueueCard/ContactInfoCard. Owns its own submit +
 * confirmation lifecycle end-to-end (like HandoffTimeoutPanel owns
 * support-requests.ts's one API call) so every trigger site is a one-line
 * mount, not a wired-up container.
 */
import { useState } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { StandardCard } from './primitives/standard-card';
import { StarIcon } from './primitives/star-icon';
import { StarRating } from './primitives/star-rating';
import { toggleSelection } from '@/lib/toggle-selection';
import {
  submitFeedback,
  FeedbackScope,
  type FeedbackSource,
} from '@/lib/api/feedback';

const GENERIC_ERROR = 'Something went wrong. Please try again.';

/** 1-5 word map in Sara's warm, plain-spoken voice — the mock shows "Great"
 *  at 4 stars; the rest fill out the same register (never clinical/survey-
 *  speak like "Satisfied"/"Very satisfied"). */
const RATING_LABELS: Record<number, string> = {
  1: 'Not great',
  2: 'Could be better',
  3: 'Good',
  4: 'Great',
  5: 'Excellent',
};

const ATTRIBUTES = ['Helpful', 'Knowledgeable', 'Quick to respond'] as const;

interface ConversationRatingCardProps {
  conversationId: string;
  source: FeedbackSource;
  /** Only meaningful for the handoff_wrap trigger — threaded through so the
   *  rating can be correlated with the handoff it followed. */
  handoffId?: string;
  /** Fires once the rating has been successfully submitted — page.tsx uses
   *  this to release a pending close-chat navigation it held back to show
   *  this card first (see its handleRequestClose). */
  onSubmitted?: () => void;
}

export function ConversationRatingCard({
  conversationId,
  source,
  handoffId,
  onSubmitted,
}: ConversationRatingCardProps) {
  const [rating, setRating] = useState(0);
  const [attributes, setAttributes] = useState<string[]>([]);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  if (submitted) {
    return (
      <StandardCard
        tone="success"
        icon={
          <Check aria-hidden="true" className="size-[15px]" strokeWidth={2.5} />
        }
        title="Thanks for the feedback"
      >
        It helps us make Art of Living support better.
      </StandardCard>
    );
  }

  const handleSubmit = async () => {
    if (rating === 0 || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitFeedback(conversationId, {
        scope: FeedbackScope.CONVERSATION,
        handoff_id: handoffId,
        rating: rating as 1 | 2 | 3 | 4 | 5,
        reasons: attributes,
        comment: comment.trim() || undefined,
        source,
      });
      setSubmitted(true);
      onSubmitted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : GENERIC_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StandardCard
      tile={
        <div className="flex size-[30px] items-center justify-center rounded-full bg-[#FBEBD8] sm:size-[34px]">
          <StarIcon size={16} filled />
        </div>
      }
      eyebrow="RATE THIS CHAT"
      title="How was your experience?"
    >
      <div className="flex flex-col gap-[14px]">
        <div className="flex items-center gap-[12px]">
          <StarRating value={rating} onChange={setRating} />
          {rating > 0 && (
            <span className="text-[13.5px] font-semibold text-[#8a7c68]">
              {RATING_LABELS[rating]}
            </span>
          )}
        </div>

        <div className="flex flex-wrap gap-[8px]">
          {ATTRIBUTES.map((attribute) => {
            const selected = attributes.includes(attribute);
            return (
              <button
                key={attribute}
                type="button"
                aria-pressed={selected}
                onClick={() =>
                  setAttributes((prev) => toggleSelection(prev, attribute))
                }
                className={cn(
                  'rounded-[20px] border-[1px] px-[13px] py-[7px] text-[12.5px] font-semibold',
                  selected
                    ? 'border-[#E8C7A0] bg-[#FFF8EF] text-[#31364E]'
                    : 'border-[#eadfce] bg-white text-[#5b6072]'
                )}
              >
                {attribute}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-[10px]">
          <input
            type="text"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add a comment (optional)"
            aria-label="Add a comment"
            className={cn(
              'flex-1 rounded-[11px] border-[1px] border-[#e6dccd] bg-[#FEFCF9] px-[14px] py-[11px]',
              'text-[13.5px] text-[#31364e] placeholder:text-[#b3a996]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e36001]/25'
            )}
          />
          <button
            type="button"
            onClick={handleSubmit}
            disabled={rating === 0 || submitting}
            className={cn(
              'rounded-[22px] bg-[#E36001] px-[20px] py-[11px] text-[13.5px] font-semibold text-white',
              'disabled:cursor-not-allowed disabled:opacity-50'
            )}
          >
            {submitting ? 'Submitting…' : 'Submit'}
          </button>
        </div>

        {error && (
          <div role="alert" className="text-[12.5px] text-[#b3261e]">
            {error}
          </div>
        )}
      </div>
    </StandardCard>
  );
}
